import type { Event } from '@sentry/react';

const sensitivePath = /^\/(?:session\/redirect|auth\/callback)(?:\/|$)/i;
const credentialKeys = new Set(['access_token', 'id_token', 'refresh_token', 'token']);

/** Password masking does not remove authentication credentials from recorded URLs. */
export function isSensitiveReplayUrl(value: string): boolean {
  try {
    const url = new URL(value, 'https://replay.invalid');
    if (sensitivePath.test(decodeURIComponent(url.pathname))) return true;

    // Replay 7 can append search after hash in its initial URL metadata.
    const parameters = `${url.search}&${url.hash.replace(/^#/, '').replace(/\?/g, '&')}`;
    for (const key of new URLSearchParams(parameters).keys()) {
      if (credentialKeys.has(key.toLowerCase())) return true;
    }
    return false;
  } catch {
    return true;
  }
}

type ReplayEvent = Event & { replay_id?: string; urls?: string[] };

/** Drop the paired replay event/recording envelope, including its DOM snapshots. */
export function createReplayPrivacyGuard() {
  const blockedReplays = new Set<string>();

  const filterEvent = (event: ReplayEvent): Event | null => {
    if (event.type !== 'replay_event') return event;

    const sensitive = event.urls?.some(isSensitiveReplayUrl);
    if (sensitive && event.replay_id) blockedReplays.add(event.replay_id);
    // Once tainted, later segments remain blocked even if their URL list is empty.
    return sensitive || (event.replay_id && blockedReplays.has(event.replay_id)) ? null : event;
  };

  return {
    filterEvent,
    blockReplay(replayId: string | undefined) {
      if (replayId) blockedReplays.add(replayId);
    },
  };
}

// Shared by SDK initialization and the auth lifecycle; no React provider is needed.
export const replayPrivacy = createReplayPrivacyGuard();
